import { z } from "zod";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";

import { Diff } from "@/features/FormCard/Diff";

import {
  AutocompleteField,
  AutocompleteLabelField,
  InputField,
} from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { ExternalLink } from "@/components/ExternalLink";
import {
  adjustDataForSubmission,
  countries_options,
  preprocessCountryData,
} from "@/components/Form/CountryPhone/data";
import { useEditForm } from "@/features/Edit/hooks";
import { Label } from "@radix-ui/react-label";
import { useTranslation } from "react-i18next";

const FormSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  country: z.string().nullable().optional(),
  sector: z.string().nullable().optional(),
});

export function K2IssuersEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    options,
    handleDelete,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
    adjustData: adjustDataForSubmission,
    adjustOnReset: preprocessCountryData,
  });

  const name = form.watch("name");

  return (
    <>
      <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
        <div className="flex justify-center w-full">
          <Card className="p-4 bg-white w-full ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardHeader className="text-lg font-bold text-gray-700">
                  {t(`${table_name}.title`, {
                    ns: "tables",
                    defaultValue: "Émetteur",
                  })}{" "}
                  {id}
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-4">
                  <Label>
                    {t(`${table_name}.sections.issuer_information`, {
                      ns: "tables",
                      defaultValue: "Informations émetteur",
                    })}
                  </Label>
                  <div className="grid grid-cols-1 gap-4">
                    <InputField
                      name="name"
                      label={t(`${table_name}.columns.name`, {
                        ns: "tables",
                        defaultValue: "Nom",
                      })}
                      isRequired={true}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteLabelField
                      name="country"
                      label={t(`${table_name}.columns.country`, {
                        ns: "tables",
                        defaultValue: "Country",
                      })}
                      options={countries_options}
                      control={form.control}
                    />
                    <AutocompleteField
                      name="sector"
                      label={t(`${table_name}.columns.sector`, {
                        ns: "tables",
                        defaultValue: "Secteur",
                      })}
                      options={options?.["sector"] ?? []}
                      control={form.control}
                    />
                  </div>
                </CardBody>
                <CardFooter>
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={false}
                  />
                </CardFooter>
              </form>
            </Form>
          </Card>
          {name && (
            <div className="absolute top-12 right-12 2xl:top-10 2xl:right-10 md:flex gap-2 flex-row hidden">
              <Button
                href={toWorkspacePath("/admin/k2/k2_static_data")}
                queryParams={{ k2_issuers_id_equal: id }}
                variant="bordered"
                color="primary"
                showAnchorIcon
                as={ExternalLink}
                radius="full"
                className="bg-white border-1 border-neutral-300 shadow-xl"
              >
                Actifs
              </Button>
            </div>
          )}

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
            children={
              changedFields
                ? Diff(changedFields, [], undefined, table_name)
                : t("no_changes")
            }
          />

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </div>
      </div>
    </>
  );
}
